import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import parseHttpError from "@/utils/parseHttpError";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import { KYCService, PropertyService, UserService } from "@/services";
import { BusinessDocument, BusinessDocumentType } from "@/types/business";
import {
  ACCEPTED_FILE_FORMATS,
  MAX_FILE_SIZE_MB,
  getDocumentNumberRule,
  normalizeKycStatus,
  sanitizeDocumentNumber,
  StagedDocument,
} from "./constants";

export const useKYCDocuments = ({
  onAllDocumentsUploaded,
}: {
  onAllDocumentsUploaded?: () => void;
} = {}) => {
  const {
    user,
    businessDetails,
    isBusinessLoading,
    refreshUser,
    refreshBusinessDetails,
    isDeveloper,
    isRealtor,
  } = useAuth();
  const ph = usePostHogAnalytics();
  const role = isDeveloper ? "developer" : isRealtor ? "realtor" : "regular-user";

  // Documents now come from the auth context business details payload, which
  // the backend enriched with the submitted documents. No separate
  // /business/documents fetch is needed anymore.
  const documents = useMemo<BusinessDocument[]>(
    () => businessDetails?.documents ?? [],
    [businessDetails],
  );

  // Locally staged documents awaiting the unified "Submit KYC documents" click.
  const [stagedDocuments, setStagedDocuments] = useState<StagedDocument[]>([]);

  // Add-new form visibility
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);

  // Add-new form state
  const [documentType, setDocumentType] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");

  // Legal name state (must match govt issued ID for verification)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Business name state (must match CAC certificate for CAC verification).
  // Synced from auth context business details (KYCService.getBusinessDetails
  // via refreshBusinessDetails) and persisted via
  // KYCService.updateBusinessDetails({ name }).
  const [businessName, setBusinessName] = useState("");

  // Edit form state
  const [editingDocument, setEditingDocument] =
    useState<BusinessDocument | null>(null);
  const [editNumber, setEditNumber] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editBusinessName, setEditBusinessName] = useState("");

  // Delete confirmation state
  const [documentToDelete, setDocumentToDelete] =
    useState<BusinessDocument | null>(null);

  // File upload state
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [editFile, setEditFile] = useState<File | null>(null);
  const [isEditUploading, setIsEditUploading] = useState(false);
  const [editUploadedFileUrl, setEditUploadedFileUrl] = useState<string | null>(
    null,
  );

  const isVerified = normalizeKycStatus(user?.kyc_status) === "verified";

  // Guard against the modal opening before the auth context finished loading
  // business details (e.g. on a hard reload straight onto the profile page).
  useEffect(() => {
    if (!isBusinessLoading && !businessDetails && user) {
      refreshBusinessDetails().catch(() => undefined);
    }
  }, [businessDetails, isBusinessLoading, user, refreshBusinessDetails]);

  // Sync legal name fields from the authenticated user's full name
  useEffect(() => {
    const nameParts = (user?.full_name ?? "").split(" ").filter(Boolean);
    setFirstName(nameParts[0] ?? "");
    setLastName(nameParts.slice(1).join(" "));
    setEditFirstName(nameParts[0] ?? "");
    setEditLastName(nameParts.slice(1).join(" "));
  }, [user?.full_name]);

  // Sync business name field from auth context business details so the CAC
  // upload card is pre-filled with the current business name.
  useEffect(() => {
    if (businessDetails?.name) {
      setBusinessName(businessDetails.name);
      setEditBusinessName((prev) => prev || businessDetails.name);
    }
  }, [businessDetails?.name]);

  const buildUserProfilePayload = (first: string, last: string) => ({
    first_name: first.trim(),
    last_name: last.trim(),
    phone_number: user?.phone_number ?? "",
    city: user?.city ?? "",
    state: user?.state ?? "",
  });

  const serverByType = useMemo(() => {
    const map: Record<string, BusinessDocument> = {};
    documents.forEach((doc) => {
      if (doc.type) map[String(doc.type).toLowerCase()] = doc;
    });
    return map;
  }, [documents]);

  const stagedByType = useMemo(() => {
    const map: Record<string, StagedDocument> = {};
    stagedDocuments.forEach((doc) => {
      map[String(doc.type).toLowerCase()] = doc;
    });
    return map;
  }, [stagedDocuments]);

  const generateFileKey = (file: File) => {
    const timestamp = new Date().getTime();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = file.name.split(".").pop();
    return `properties/${timestamp}-${randomString}.${extension}`;
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    try {
      const key = generateFileKey(file);
      const { data } = await PropertyService.GetUploadUrl(key);
      const { upload_url, metadata } = data?.data;
      await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      return metadata?.url ?? null;
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  };

  const handleFile = async (newFile: File | null, isEdit = false) => {
    if (!newFile) return;
    if (!ACCEPTED_FILE_FORMATS.includes(newFile.type)) {
      toast.error(`File type ${newFile.type} is not supported`);
      return;
    }
    if (newFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File is larger than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    if (isEdit) {
      setIsEditUploading(true);
      setEditFile(newFile);
      const url = await uploadFile(newFile);
      setEditUploadedFileUrl(url);
      setIsEditUploading(false);
    } else {
      setIsUploading(true);
      setFile(newFile);
      const url = await uploadFile(newFile);
      setUploadedFileUrl(url);
      setIsUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    setUploadedFileUrl(null);
  };

  const removeEditFile = () => {
    setEditFile(null);
    setEditUploadedFileUrl(null);
  };

  const resetAddForm = () => {
    setDocumentType("");
    setDocumentNumber("");
    setFile(null);
    setUploadedFileUrl(null);
  };

  const handleDocumentNumberChange = (value: string) => {
    setDocumentNumber(sanitizeDocumentNumber(value, documentType));
  };

  const handleEditNumberChange = (value: string) => {
    setEditNumber(sanitizeDocumentNumber(value, editingDocument?.type));
  };

  const startAddDocument = (type: string) => {
    setDocumentType(type);
    setDocumentNumber("");
    setFile(null);
    setUploadedFileUrl(null);
    setIsAddFormOpen(true);
  };

  const cancelAddDocument = () => {
    resetAddForm();
    setIsAddFormOpen(false);
  };

  const isBusinessDocumentType = (type?: string | null) =>
    (type ?? "").toLowerCase() === BusinessDocumentType.CAC;

  const stageDocument = () => {
    const type = documentType.toLowerCase() as BusinessDocumentType;
    const rule = getDocumentNumberRule(documentType);
    if (rule && !rule.pattern.test(documentNumber.trim())) {
      toast.error(rule.error);
      return;
    }
    if (isBusinessDocumentType(documentType)) {
      if (!businessName.trim()) {
        toast.error("Please provide your business name");
        return;
      }
    } else if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please provide your first and last name");
      return;
    }
    if (!uploadedFileUrl) {
      toast.error("Please upload the document file");
      return;
    }

    setStagedDocuments((prev) => {
      const next = prev.filter((doc) => String(doc.type) !== String(type));
      return [
        ...next,
        { type, number: documentNumber.trim(), upload_url: uploadedFileUrl },
      ];
    });
    ph.trackEvent("kyc_document_added", { document_type: type, role });
    toast.success(
      `${documentType.toUpperCase()} document added. Submit your documents when ready.`,
    );
    resetAddForm();
    setIsAddFormOpen(false);
  };

  const removeStagedDocument = (type: BusinessDocumentType) => {
    setStagedDocuments((prev) =>
      prev.filter((doc) => String(doc.type) !== String(type)),
    );
    toast.success(`${String(type).toUpperCase()} document removed`);
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (stagedDocuments.length === 0) {
        throw new Error("No documents to submit");
      }
      const needsPersonalName = stagedDocuments.some(
        (doc) => !isBusinessDocumentType(String(doc.type)),
      );
      const needsBusinessName = stagedDocuments.some((doc) =>
        isBusinessDocumentType(String(doc.type)),
      );
      if (needsPersonalName && (!firstName.trim() || !lastName.trim())) {
        throw new Error("Please provide your first and last name");
      }
      if (needsBusinessName && !businessName.trim()) {
        throw new Error("Please provide your business name");
      }
      if (needsPersonalName) {
        await UserService.updateUserProfile(
          buildUserProfilePayload(firstName, lastName),
        );
      }
      if (needsBusinessName) {
        await KYCService.updateBusinessDetails({ name: businessName.trim() });
      }
      for (const doc of stagedDocuments) {
        await KYCService.addBusinessDocument({
          type: doc.type,
          number: doc.number,
          upload_url: doc.upload_url,
        });
      }
    },
    onSuccess: async () => {
      toast.success("KYC documents submitted for verification");
      ph.trackEvent("kyc_submitted", {
        doc_count: stagedDocuments.length,
        role,
      });
      setStagedDocuments([]);
      refreshUser().catch(() => undefined);
      await refreshBusinessDetails().catch(() => undefined);
      onAllDocumentsUploaded?.();
    },
    onError: (error: any) => {
      toast.error(error?.message || "Request failed");
    },
  });

  const startEdit = (doc: BusinessDocument) => {
    setEditingDocument(doc);
    setEditNumber(
      sanitizeDocumentNumber(doc.identification_number ?? "", doc.type),
    );
    setEditFile(null);
    setEditUploadedFileUrl(null);
    const nameParts = (user?.full_name ?? "").split(" ").filter(Boolean);
    setEditFirstName(nameParts[0] ?? "");
    setEditLastName(nameParts.slice(1).join(" "));
    setEditBusinessName(businessDetails?.name ?? businessName ?? "");
  };

  const cancelEdit = () => {
    setEditingDocument(null);
    setEditNumber("");
    setEditFile(null);
    setEditUploadedFileUrl(null);
    setEditFirstName("");
    setEditLastName("");
    setEditBusinessName("");
  };

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingDocument) return;
      const isBusinessDoc = isBusinessDocumentType(editingDocument.type);
      if (isBusinessDoc) {
        if (!editBusinessName.trim()) {
          throw new Error("Please provide your business name");
        }
      } else if (!editFirstName.trim() || !editLastName.trim()) {
        throw new Error("Please provide your first and last name");
      }
      const rule = getDocumentNumberRule(editingDocument.type);
      if (rule && !rule.pattern.test(editNumber.trim())) {
        throw new Error(rule.error);
      }
      if (isBusinessDoc) {
        await KYCService.updateBusinessDetails({
          name: editBusinessName.trim(),
        });
      } else {
        await UserService.updateUserProfile(
          buildUserProfilePayload(editFirstName, editLastName),
        );
      }
      const response = await KYCService.editBusinessDocument(
        editingDocument.id,
        {
          number: editNumber,
          upload_url: editUploadedFileUrl ?? undefined,
        },
      );
      return response?.data;
    },
    onSuccess: async () => {
      toast.success("Document updated successfully");
      refreshUser().catch(() => undefined);
      cancelEdit();
      await refreshBusinessDetails().catch(() => undefined);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Request failed");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      await KYCService.deleteBusinessDocument(documentId);
    },
    onSuccess: async () => {
      toast.success("Document deleted successfully");
      setDocumentToDelete(null);
      if (editingDocument) cancelEdit();
      await refreshBusinessDetails().catch(() => undefined);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Request failed");
    },
  });

  // Realtor fast path: realtors only ever submit a single document (NIN *or*
  // CAC), so the add form submits it immediately instead of staging it first
  // and asking for a second "Submit KYC documents" click. Developers keep the
  // normal stage-then-submit-together flow via stageDocument/submitMutation.
  const directSubmitMutation = useMutation({
    mutationFn: async (doc: StagedDocument & { firstName: string; lastName: string; businessName: string }) => {
      const isBusinessDoc = isBusinessDocumentType(String(doc.type));
      if (isBusinessDoc) {
        if (!doc.businessName.trim()) {
          throw new Error("Please provide your business name");
        }
      } else if (!doc.firstName.trim() || !doc.lastName.trim()) {
        throw new Error("Please provide your first and last name");
      }
      if (isBusinessDoc) {
        await KYCService.updateBusinessDetails({
          name: doc.businessName.trim(),
        });
      } else {
        await UserService.updateUserProfile(
          buildUserProfilePayload(doc.firstName, doc.lastName),
        );
      }
      await KYCService.addBusinessDocument({
        type: doc.type,
        number: doc.number,
        upload_url: doc.upload_url,
      });
      return doc;
    },
    onSuccess: async (doc) => {
      toast.success("KYC document submitted for verification");
      ph.trackEvent("kyc_document_added", {
        document_type: String(doc.type),
        role,
      });
      ph.trackEvent("kyc_submitted", {
        doc_count: 1,
        document_type: String(doc.type),
        role,
      });
      resetAddForm();
      setIsAddFormOpen(false);
      refreshUser().catch(() => undefined);
      await refreshBusinessDetails().catch(() => undefined);
      onAllDocumentsUploaded?.();
    },
    onError: (error: any) => {
      toast.error(error?.message || "Request failed");
    },
  });

  const submitRealtorDocument = () => {
    if (directSubmitMutation.isPending || isUploading) return;
    if (!documentType) {
      toast.error("Please select a document type");
      return;
    }
    const type = documentType.toLowerCase() as BusinessDocumentType;
    const rule = getDocumentNumberRule(documentType);
    if (rule && !rule.pattern.test(documentNumber.trim())) {
      toast.error(rule.error);
      return;
    }
    if (isBusinessDocumentType(documentType)) {
      if (!businessName.trim()) {
        toast.error("Please provide your business name");
        return;
      }
    } else if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please provide your first and last name");
      return;
    }
    if (!uploadedFileUrl) {
      toast.error("Please upload the document file");
      return;
    }
    directSubmitMutation.mutate({
      type,
      number: documentNumber.trim(),
      upload_url: uploadedFileUrl,
      firstName,
      lastName,
      businessName,
    });
  };

  return {
    documents,
    isLoading: isBusinessLoading,
    isVerified,
    isDeveloper,
    isAddFormOpen,
    setIsAddFormOpen,
    serverByType,
    stagedByType,
    stagedDocuments,
    // Add/stage form
    documentType,
    setDocumentType,
    documentNumber,
    setDocumentNumber,
    handleDocumentNumberChange,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    businessName,
    setBusinessName,
    isBusinessDocumentType,
    file,
    isUploading,
    uploadedFileUrl,
    handleFile,
    removeFile,
    startAddDocument,
    cancelAddDocument,
    stageDocument,
    removeStagedDocument,
    submitMutation,
    directSubmitMutation,
    submitRealtorDocument,
    // Edit form
    editingDocument,
    editNumber,
    setEditNumber,
    handleEditNumberChange,
    editFirstName,
    setEditFirstName,
    editLastName,
    setEditLastName,
    editBusinessName,
    setEditBusinessName,
    editFile,
    isEditUploading,
    removeEditFile,
    startEdit,
    cancelEdit,
    // Delete
    documentToDelete,
    setDocumentToDelete,
    // Mutations
    editMutation,
    deleteMutation,
  };
};
